import { useMemo, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlarmClock,
  ArrowUpRight,
  BarChart3,
  Building2,
  ChevronRight,
  Folder,
  FlaskConical,
  Inbox,
  LayoutGrid,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Star,
  Timer,
  UserCog,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { QUEUES } from '@/lib/nav'
import { useAuth } from '@/providers/AuthProvider'
import { useBoardFolders, useBoards } from '@/hooks/useBoards'
import { useProducts } from '@/hooks/useDirectory'
import { useProductNav } from '@/hooks/useProductNav'
import { useSidebarPrefs } from '@/hooks/useSidebarPrefs'
import { arrangeProducts } from '@/lib/productNav'
import { boardTree, type FolderNode } from '@/lib/boardTree'
import { PRODUCT_ICONS, ProductNavDialog } from '@/components/layout/ProductNavDialog'
import { BoardIcon } from '@/components/board/BoardIcon'
import type { Board } from '@/api/boardsApi'
import { useTicketCounts } from '@/hooks/useTicketCounts'
import { queueOfBoardId } from '@/lib/queueView'
import title21Mark from '@/assets/title21-mark.png'

const QUEUE_ICONS: Record<string, LucideIcon> = {
  all: Inbox,
  mine: UserRound,
  sla_risk: AlarmClock,
  gxp: FlaskConical,
  escalated: ArrowUpRight,
}

/** Boards listed outside folders before "All boards" takes over. */
const UNFILED_SHOWN = 6

// The active item is lit in the brand tint with a bar on its left edge, as a tab in cgt-frontend.
const itemBase =
  'group/item relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-body transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
const itemIdle = 'text-sidebar-text hover:bg-surface-subtle hover:text-ink'
const itemActive =
  "bg-sidebar-active font-medium text-accent-foreground before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-primary before:content-['']"

function Item({ to, active, icon: Icon, glyph: own, label, count, warn, rail, indent }: {
  to: string
  active: boolean
  icon?: LucideIcon
  /** An already-rendered icon instead (a board's own, in its colour). */
  glyph?: ReactNode
  label: string
  count?: number
  warn?: boolean
  /** The collapsed rail: icon only, the label as a tooltip. */
  rail: boolean
  indent?: boolean
}) {
  const glyph = Icon ? (
    <Icon className={cn('size-4 shrink-0', active ? 'text-primary' : 'text-sidebar-subtle group-hover/item:text-ink')} aria-hidden />
  ) : own ? (
    own
  ) : rail ? (
    // No icon (a product without one): its initial, so the rail still has something to click.
    <span aria-hidden className="flex size-4 shrink-0 items-center justify-center rounded-[4px] bg-surface-subtle text-[10px] font-semibold text-sidebar-subtle">
      {label.slice(0, 1).toUpperCase()}
    </span>
  ) : null
  const badge =
    typeof count === 'number' && count > 0 ? (
      rail ? (
        <span aria-hidden className={cn('absolute right-1 top-1 size-1.5 rounded-full', warn ? 'bg-sidebar-warn' : 'bg-primary')} />
      ) : (
        <span
          className={cn(
            'min-w-6 rounded-full px-1.5 text-center font-mono text-label tabular-nums',
            active ? 'bg-primary text-primary-foreground' : warn ? 'bg-status-customer-bg text-sidebar-warn' : 'text-sidebar-subtle',
          )}
        >
          {count}
        </span>
      )
    ) : !rail && typeof count === 'number' ? (
      <span className="min-w-6 px-1.5 text-center font-mono text-label tabular-nums text-sidebar-subtle">{count}</span>
    ) : null
  return (
    // Link, not NavLink: NavLink matches on the path alone, so every /queue?queue=… entry would claim aria-current.
    <Link
      to={to}
      title={rail ? (typeof count === 'number' ? `${label} · ${count}` : label) : undefined}
      aria-label={rail ? label : undefined}
      className={cn(itemBase, active ? itemActive : itemIdle, rail && 'justify-center px-0 py-2', indent && !rail && 'pl-7')}
      aria-current={active ? 'page' : undefined}
    >
      {glyph}
      {rail ? null : <span className="min-w-0 grow truncate">{label}</span>}
      {badge}
    </Link>
  )
}

/** A section: a foldable heading over its items; on the rail, a thin divider instead. */
function Section({ id, label, rail, folded, onFold, action, children }: {
  id: string
  label: string
  rail: boolean
  folded: boolean
  onFold: () => void
  action?: ReactNode
  children: ReactNode
}) {
  if (rail)
    return (
      <nav aria-label={label} className="flex flex-col gap-0.5 border-t border-sidebar-border pt-3 first:border-t-0 first:pt-0">
        {children}
      </nav>
    )
  return (
    <nav aria-label={label} className="group/section flex flex-col gap-0.5">
      <div className="flex items-center pr-1">
        <button
          type="button"
          onClick={onFold}
          aria-expanded={!folded}
          aria-controls={`nav-${id}`}
          className="flex grow items-center gap-1 rounded px-2.5 py-1 text-eyebrow font-medium uppercase text-sidebar-heading hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
          <ChevronRight className={cn('size-3 opacity-0 transition group-hover/section:opacity-100', !folded && 'rotate-90')} aria-hidden />
        </button>
        {action}
      </div>
      {folded ? null : (
        <div id={`nav-${id}`} className="flex flex-col gap-0.5">
          {children}
        </div>
      )}
    </nav>
  )
}

/** A small hover action beside a section heading (customize, new). */
function HeadingAction({ label, icon: Icon, onClick, to }: { label: string; icon: LucideIcon; onClick?: () => void; to?: string }) {
  const cls =
    'rounded p-1 text-sidebar-subtle opacity-0 hover:bg-surface-subtle hover:text-ink focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/section:opacity-100'
  return to ? (
    <Link to={to} aria-label={label} title={label} className={cls}>
      <Icon className="size-3.5" aria-hidden />
    </Link>
  ) : (
    <button type="button" onClick={onClick} aria-label={label} title={label} className={cls}>
      <Icon className="size-3.5" aria-hidden />
    </button>
  )
}

/** A folder in the Boards section: foldable, its boards indented under it. */
function FolderGroup({ node, team, folded, onFold, activeBoard }: {
  node: FolderNode<Board>
  team: boolean
  folded: boolean
  onFold: () => void
  activeBoard: string | null
}) {
  const { t } = useTranslation()
  const FolderIcon = team ? Users : Folder
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={onFold}
        aria-expanded={!folded}
        className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-body text-sidebar-text hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <FolderIcon className="size-4 shrink-0 text-sidebar-subtle" aria-hidden />
        <span className="min-w-0 grow truncate text-left">{node.folder.name}</span>
        <span className="sr-only">{team ? t('nav.teamFolder') : t('nav.myFolder')}</span>
        <ChevronRight className={cn('size-3.5 shrink-0 text-sidebar-subtle transition', !folded && 'rotate-90')} aria-hidden />
      </button>
      {folded ? null : node.boards.length === 0 ? (
        <p className="py-1 pl-9 text-label text-sidebar-subtle">{t('nav.folderEmpty')}</p>
      ) : (
        node.boards.map((bd) => (
          <Item
            key={bd.id}
            rail={false}
            indent
            to={`/boards/${bd.id}`}
            active={activeBoard === bd.id}
            glyph={<BoardIcon icon={bd.icon} color={bd.color} className="size-3.5" />}
            label={bd.name}
          />
        ))
      )}
    </div>
  )
}

export function Sidebar() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const location = useLocation()
  const { prefs, toggleCollapsed, toggle, isFolded } = useSidebarPrefs()
  const rail = prefs.collapsed
  const { data: counts } = useTicketCounts()
  // The catalog's active products (GET /products), so one added in admin shows up here.
  const { data: products = [] } = useProducts()
  // Each person's own order / icons / hidden products for that section.
  const [productNav, setProductNav] = useProductNav(user?.id)
  const productRows = useMemo(() => arrangeProducts(products, productNav), [products, productNav])
  const [customizing, setCustomizing] = useState(false)
  const canAnalytics = usePermission(PERMISSIONS.analyticsRead)
  const canSla = usePermission(PERMISSIONS.slaManage)
  const canRouting = usePermission(PERMISSIONS.routingManage)
  const canAdminSla = canSla || canRouting
  // Each hook on its own line: `||` would skip later hooks and break hook order.
  const canUsers = usePermission(PERMISSIONS.usersManage)
  const canRoles = usePermission(PERMISSIONS.rolesManage)
  const canTeams = usePermission(PERMISSIONS.teamsManage)
  const canAccess = canUsers || canRoles || canTeams
  // The catalog (products, clients, tenants) is superadmin-only — role permissions don't grant it.
  const isSuperAdmin = !!user?.isSuperAdmin
  const canCreate = usePermission(PERMISSIONS.ticketsCreate)
  const isCustomer = user?.kind === 'CUSTOMER'
  // Boards and folders are for agents; a customer never asks for them.
  const { data: boards = [] } = useBoards(!isCustomer)
  const { data: folders } = useBoardFolders(!isCustomer)
  const tree = useMemo(() => boardTree(boards, folders?.personal ?? [], folders?.team?.folders ?? []), [boards, folders])
  // Customers see their organisation's tickets only: no agent work queues.
  const queues = isCustomer ? QUEUES.filter((q) => q.key === 'all') : QUEUES

  const params = new URLSearchParams(location.search)
  const onQueue = location.pathname === '/queue'
  const boardId = location.pathname.startsWith('/boards/') ? location.pathname.slice('/boards/'.length) : null
  // The queue's cards (its built-in board, /boards/queue-<key>) light the same item.
  const boardQueue = boardId ? queueOfBoardId(boardId) : null
  const activeQueue = boardQueue ?? (onQueue && !params.get('product') ? (params.get('queue') ?? 'all') : null)
  const activeProduct = onQueue ? params.get('product') : null
  const at = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  const boardItem = (bd: Board) => (
    <Item
      key={bd.id}
      rail={rail}
      to={`/boards/${bd.id}`}
      active={boardId === bd.id}
      glyph={<BoardIcon icon={bd.icon} color={bd.color} />}
      label={bd.name}
    />
  )
  const ToggleIcon = rail ? PanelLeftOpen : PanelLeftClose
  const toggleLabel = rail ? t('nav.expandSidebar') : t('nav.collapseSidebar')

  return (
    <aside
      aria-label={t('nav.sidebar')}
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 ease-out motion-reduce:transition-none',
        rail ? 'w-[60px]' : 'w-[264px]',
      )}
    >
      <div className={cn('flex h-14 shrink-0 items-center gap-2.5 border-b border-sidebar-border', rail ? 'justify-center px-2' : 'px-4')}>
        {rail ? null : (
          <Link to="/queue" className="flex min-w-0 grow items-center gap-2.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <img src={title21Mark} alt="" className="h-6 w-auto" />
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-brand font-semibold text-ink">{t('brand.name')}</span>
              <span className="truncate text-label text-sidebar-subtle">{t('brand.product')}</span>
            </span>
          </Link>
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={toggleLabel}
          aria-keyshortcuts="["
          title={`${toggleLabel}  [`}
          className="rounded-md p-1.5 text-sidebar-subtle hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ToggleIcon className="size-4" aria-hidden />
        </button>
      </div>

      <div className={cn('flex min-h-0 grow flex-col gap-5 overflow-y-auto overflow-x-hidden py-4', rail ? 'px-2' : 'px-3')}>
        {canCreate ? (
          <nav aria-label={t('nav.tickets')} className="flex flex-col">
          <NavLink
            to="/tickets/new"
            title={rail ? (isCustomer ? t('nav.newTicket') : t('nav.logTicket')) : undefined}
            aria-label={rail ? (isCustomer ? t('nav.newTicket') : t('nav.logTicket')) : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center justify-center gap-2 rounded-md py-2 text-body font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                rail ? 'px-0' : 'px-3',
                isActive ? 'bg-primary/90 text-primary-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90',
              )
            }
          >
            <Plus className="size-4" aria-hidden />
            {rail ? null : isCustomer ? t('nav.newTicket') : t('nav.logTicket')}
          </NavLink>
          </nav>
        ) : null}

        <Section id="queues" label={t('nav.queues')} rail={rail} folded={isFolded('queues')} onFold={() => toggle('queues')}>
          {queues.map((q) => (
            <Item
              key={q.key}
              rail={rail}
              to={q.to}
              active={activeQueue === q.key}
              icon={QUEUE_ICONS[q.key]}
              label={t(isCustomer ? 'nav.myOrgTickets' : q.labelKey)}
              count={counts?.[q.count]}
              warn={q.warn}
            />
          ))}
        </Section>

        {!isCustomer ? (
          <Section
            id="boards"
            label={t('nav.boards')}
            rail={rail}
            folded={isFolded('boards')}
            onFold={() => toggle('boards')}
            action={<HeadingAction label={t('nav.manageBoards')} icon={Plus} to="/boards?new=1" />}
          >
            {rail ? (
              // The rail keeps it short: starred boards, then the way in to all of them.
              tree.starred.map(boardItem)
            ) : (
              <>
                {tree.starred.length ? (
                  <>
                    <p className="flex items-center gap-1.5 px-2.5 pt-0.5 text-label text-sidebar-subtle">
                      <Star className="size-3" aria-hidden />
                      {t('nav.starred')}
                    </p>
                    {tree.starred.map(boardItem)}
                  </>
                ) : null}
                {tree.team.map((node) => (
                  <FolderGroup key={node.folder.id} node={node} team folded={isFolded(node.folder.id)} onFold={() => toggle(node.folder.id)} activeBoard={boardId} />
                ))}
                {tree.personal.map((node) => (
                  <FolderGroup key={node.folder.id} node={node} team={false} folded={isFolded(node.folder.id)} onFold={() => toggle(node.folder.id)} activeBoard={boardId} />
                ))}
                {tree.unfiled.slice(0, UNFILED_SHOWN).map(boardItem)}
              </>
            )}
            <Item rail={rail} to="/boards" active={location.pathname === '/boards'} icon={LayoutGrid} label={t('nav.allBoards')} />
          </Section>
        ) : null}

        {products.length > 0 ? (
          <Section
            id="products"
            label={t('nav.byProduct')}
            rail={rail}
            folded={isFolded('products')}
            onFold={() => toggle('products')}
            action={<HeadingAction label={t('nav.productNav.customize')} icon={Pencil} onClick={() => setCustomizing(true)} />}
          >
            {productRows
              .filter((r) => !r.hidden)
              .map(({ product: p, icon }) => (
                <Item key={p.key} rail={rail} to={`/queue?product=${p.key}`} active={activeProduct === p.key} icon={icon ? PRODUCT_ICONS[icon] : undefined} label={p.name} />
              ))}
            {!rail && productRows.every((r) => r.hidden) ? <p className="px-2.5 text-label text-sidebar-subtle">{t('nav.productNav.allHidden')}</p> : null}
          </Section>
        ) : null}
        <ProductNavDialog open={customizing} products={products} prefs={productNav} onOpenChange={setCustomizing} onSave={setProductNav} />

        {canAnalytics || canAdminSla || canAccess || isSuperAdmin ? (
          <Section id="admin" label={t('nav.admin')} rail={rail} folded={isFolded('admin')} onFold={() => toggle('admin')}>
            {canAnalytics ? <Item rail={rail} to="/analytics" active={at('/analytics')} icon={BarChart3} label={t('nav.analytics')} /> : null}
            {canAdminSla ? <Item rail={rail} to="/admin/sla" active={at('/admin/sla')} icon={Timer} label={t('nav.slaPolicies')} /> : null}
            {canAccess ? <Item rail={rail} to="/admin/access" active={at('/admin/access')} icon={UserCog} label={t('nav.access')} /> : null}
            {isSuperAdmin ? (
              <>
                <Item rail={rail} to="/admin/products" active={at('/admin/products')} icon={Package} label={t('nav.catalogProducts')} />
                <Item rail={rail} to="/admin/clients" active={at('/admin/clients')} icon={Building2} label={t('nav.catalogClients')} />
              </>
            ) : null}
          </Section>
        ) : null}
      </div>
    </aside>
  )
}
